import { useRef, useEffect, useCallback } from 'react';
import { markConversationSeen } from '../MessagesServices';

/**
 * Tracks which messages have been marked as seen to prevent duplicate API calls.
 * Automatically marks new incoming customer/support messages as seen.
 * 
 * @param {Object} selectedConversation - The currently selected conversation
 * @param {Function} setConversations - Function to update the conversations list
 * @returns {Object} { markMessageAsSeen }
 */
const useMessageSeenTracking = (selectedConversation, setConversations) => {
    // Track which messages we've already marked as seen in this session
    const seenMessageIdsRef = useRef(new Set());

    // Clear seen messages tracking when switching conversations
    useEffect(() => {
        seenMessageIdsRef.current.clear();
    }, [selectedConversation?.id]);

    const markMessageAsSeen = useCallback((message) => {
        // Only mark as seen for incoming customer/support messages, not those sent by agent
        if (!message?.id) return;

        // Prevent duplicate /seen calls for the same message id
        if (seenMessageIdsRef.current.has(message.id)) return;
        seenMessageIdsRef.current.add(message.id);

        // Update local conversation state immediately for responsive UI
        setConversations(prevConversations => {
            const updated = prevConversations.map(conv =>
                conv.id === selectedConversation.id
                    ? { ...conv, is_seen_by_user: true }
                    : conv
            );
            return updated;
        });

        // Fire and forget; no await to avoid blocking and piling up
        markConversationSeen(selectedConversation.id, true, message.id).catch(() => { });
    }, [selectedConversation?.id, setConversations]);

    return {
        markMessageAsSeen
    };
};

export default useMessageSeenTracking;
